import { PRICE_TYPES } from '@/components/common/PricingSectionAndDiscounts/constants';

import {
  maxAmountForCurrencyInCents,
  minAmountForCurrencyInCents
} from '@/pages/portal/utils';

import { t } from './localization';

type Currency = string;

type ValidatePriceInputsProps = {
  amount: number;
  currency: Currency;
  minAmount: number;
  priceType: (typeof PRICE_TYPES)[keyof typeof PRICE_TYPES];
  suggestedAmount: number;
};

export const isAmountWithinRange = (
  amount: number,
  currency: Currency
): boolean => {
  const min = minAmountForCurrencyInCents(currency) / 100;
  const max = maxAmountForCurrencyInCents(currency) / 100;
  return amount >= min && amount <= max;
};

const showInvalidAmountError = (
  currency: Currency,
  min: number,
  max: number
): string => {
  return t(
    'price-must-be-higher-than-minAmount-and-lower-than-maxAmount-please-input-valid-amount',
    { currency, minAmount: min, maxAmount: max }
  );
};

export const validateFixedPriceInput = ({
  amount,
  currency
}: {
  amount: number;
  currency: string;
}) => {
  const min = minAmountForCurrencyInCents(currency) / 100;
  const max = maxAmountForCurrencyInCents(currency) / 100;

  if (amount != 0 && !isAmountWithinRange(amount, currency)) {
    return showInvalidAmountError(currency, min, max);
  }

  return '';
};

export const validateFlexiblePriceInputs = ({
  minAmount,
  suggestedAmount,
  currency
}: {
  currency: string;
  minAmount: number;
  suggestedAmount: number;
}) => {
  const min = minAmountForCurrencyInCents(currency) / 100;
  const max = maxAmountForCurrencyInCents(currency) / 100;
  const minAmountNumber = Number(minAmount);
  const suggestedAmountNumber = Number(suggestedAmount);

  if (
    minAmountNumber != 0 &&
    !isAmountWithinRange(minAmountNumber, currency)
  ) {
    return showInvalidAmountError(currency, min, max);
  }

  if (
    suggestedAmountNumber != 0 &&
    !isAmountWithinRange(suggestedAmountNumber, currency)
  ) {
    return showInvalidAmountError(currency, min, max);
  }

  if (minAmountNumber > suggestedAmountNumber) {
    return t('minimum-amount-cannot-be-higher-than-suggested-amount');
  }

  return '';
};

export const validatePriceInputs = ({
  priceType,
  amount,
  minAmount,
  suggestedAmount,
  currency
}: ValidatePriceInputsProps): string => {
  if (priceType === PRICE_TYPES.FIXED) {
    return validateFixedPriceInput({ amount, currency });
  }

  if (priceType === PRICE_TYPES.FLEXIBLE) {
    return validateFlexiblePriceInputs({
      minAmount: minAmount,
      suggestedAmount: suggestedAmount,
      currency
    });
  }

  return '';
};
